const scale = {
    name: "scale",
    path: "/scale",
    title: "scale",
    description: "Utilities for controlling scale transforms.",
    value: {
        "scale": {
            "scale-none": "none",
            "scale-<number>": "<number>% <number>%",
            "-scale-<number>": "calc(<number>% * -1) calc(<number>% * -1)",
            "scale-x-<number>": "<number>% var(--uni-scale-y)",
            "-scale-x-<number>": "calc(<number>% * -1) var(--uni-scale-y)",
            "scale-y-<number>": "var(--uni-scale-x) <number>%",
            "-scale-y-<number>": "var(--uni-scale-x) calc(<number>% * -1)",
        }
    },
    exampleData: [
        {
            example: {
                code: `<img class="scale-75 ..." src="/img/mountains.jpg" />
<img class="scale-100 ..." src="/img/mountains.jpg" />
<img class="scale-125 ..." src="/img/mountains.jpg" />`
            }
        },
        {
            example: {
                code: `<img class="scale-x-75 ..." src="/img/mountains.jpg" />
<img class="scale-x-100 ..." src="/img/mountains.jpg" />
<img class="scale-x-125 ..." src="/img/mountains.jpg" />`
            }
        },
        {
            example: {
                code: `<img class="scale-y-75 ..." src="/img/mountains.jpg" />
<img class="scale-y-100 ..." src="/img/mountains.jpg" />
<img class="scale-y-125 ..." src="/img/mountains.jpg" />`
            }
        },
    ],
    htmlData: [
        {
            title: "Scaling elements",
            data: `
      <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
        Use <code class="text-color-gray-800 dark:text-color-white font-semibold">scale-<var>&lt;number&gt;</var></code> utilities like <code class="text-color-blue-500">scale-150</code> and <code class="text-color-blue-500">scale-50</code> to scale an element:
      </p>
      <div class="flex gap-8 items-end mb-6 justify-around">
        <div class="flex flex-col items-center">
          <span class="mb-8 text-xs text-color-gray-500">scale-75</span>
          <div class="relative">
            <div class="absolute inset-0">
              <img class="size-32 opacity-25 rounded-lg object-cover opacity-25" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo"/>
            </div>
            <img class="scale-75 size-32 rounded-lg shadow-lg" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo" alt="mountains" />
          </div>
        </div>
        <div class="flex flex-col items-center">
          <span class="mb-8 text-xs text-color-gray-500">-scale-75</span>
          <div class="relative">
            <div class="absolute inset-0">
              <img class="size-32 opacity-25 rounded-lg object-cover opacity-25" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo"/>
            </div>
            <img class="-scale-75 size-32 rounded-lg shadow-lg" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo" alt="mountains" />
          </div>
        </div>
        <div class="flex flex-col items-center">
          <span class="mb-8 text-xs text-color-gray-500">scale-100</span>
          <div class="relative">
            <div class="absolute inset-0">
              <img class="size-32 opacity-25 rounded-lg object-cover opacity-25" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo"/>
            </div>
            <img class="scale-100 size-32 rounded-lg shadow-lg" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo" alt="mountains" />
          </div>
        </div>
        <div class="flex flex-col items-center">
          <span class="mb-8 text-xs text-color-gray-500">scale-125</span>
          <div class="relative">
            <div class="absolute inset-0">
              <img class="size-32 opacity-25 rounded-lg object-cover opacity-25" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo"/>
            </div>
          <img class="scale-125 size-32 rounded-lg shadow-lg" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo" alt="mountains" />
          </div>
        </div>
      </div>
      `
        },
        {
            title: "Scaling elements",
            data: `
      <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
        Use <code class="text-color-gray-800 dark:text-color-white font-semibold">scale-x-<var>&lt;number&gt;</var></code> utilities like <code class="text-color-blue-500">scale-x-150</code> and <code class="text-color-blue-500">scale-x-50</code> to scale an element:
      </p>
      <div class="flex gap-8 items-end mb-6 justify-around">
        <div class="flex flex-col items-center">
          <span class="mb-8 text-xs text-color-gray-500">scale-x-75</span>
          <div class="relative">
            <div class="absolute inset-0">
              <img class="size-32 opacity-25 rounded-lg object-cover opacity-25" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo"/>
            </div>
            <img class="scale-x-75 size-32 rounded-lg shadow-lg" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo" alt="mountains" />
          </div>
        </div>
        <div class="flex flex-col items-center">
          <span class="mb-8 text-xs text-color-gray-500">-scale-x-75</span>
          <div class="relative">
            <div class="absolute inset-0">
              <img class="size-32 opacity-25 rounded-lg object-cover opacity-25" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo"/>
            </div>
            <img class="-scale-x-75 size-32 rounded-lg shadow-lg" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo" alt="mountains" />
          </div>
        </div>
        <div class="flex flex-col items-center">
          <span class="mb-8 text-xs text-color-gray-500">scale-x-100</span>
          <div class="relative">
            <div class="absolute inset-0">
              <img class="size-32 opacity-25 rounded-lg object-cover opacity-25" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo"/>
            </div>
            <img class="scale-x-100 size-32 rounded-lg shadow-lg" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo" alt="mountains" />
          </div>
        </div>
        <div class="flex flex-col items-center">
          <span class="mb-8 text-xs text-color-gray-500">scale-x-125</span>
          <div class="relative">
            <div class="absolute inset-0">
              <img class="size-32 opacity-25 rounded-lg object-cover opacity-25" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo"/>
            </div>
          <img class="scale-x-125 size-32 rounded-lg shadow-lg" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo" alt="mountains" />
          </div>
        </div>
      </div>
      `
        },
        {
            title: "Scaling elements",
            data: `
      <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
        Use <code class="text-color-gray-800 dark:text-color-white font-semibold">scale-y-<var>&lt;number&gt;</var></code> utilities like <code class="text-color-blue-500">scale-y-150</code> and <code class="text-color-blue-500">scale-y-50</code> to scale an element:
      </p>
      <div class="flex gap-8 items-end mb-6 justify-around">
        <div class="flex flex-col items-center">
          <span class="mb-8 text-xs text-color-gray-500">scale-y-75</span>
          <div class="relative">
            <div class="absolute inset-0">
              <img class="size-32 opacity-25 rounded-lg object-cover opacity-25" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo"/>
            </div>
            <img class="scale-y-75 size-32 rounded-lg shadow-lg" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo" alt="mountains" />
          </div>
        </div>
        <div class="flex flex-col items-center">
          <span class="mb-8 text-xs text-color-gray-500">-scale-y-75</span>
          <div class="relative">
            <div class="absolute inset-0">
              <img class="size-32 opacity-25 rounded-lg object-cover opacity-25" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo"/>
            </div>
            <img class="-scale-y-75 size-32 rounded-lg shadow-lg" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo" alt="mountains" />
          </div>
        </div>
        <div class="flex flex-col items-center">
          <span class="mb-8 text-xs text-color-gray-500">scale-y-100</span>
          <div class="relative">
            <div class="absolute inset-0">
              <img class="size-32 opacity-25 rounded-lg object-cover opacity-25" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo"/>
            </div>
            <img class="scale-y-100 size-32 rounded-lg shadow-lg" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo" alt="mountains" />
          </div>
        </div>
        <div class="flex flex-col items-center">
          <span class="mb-8 text-xs text-color-gray-500">scale-x-125</span>
          <div class="relative">
            <div class="absolute inset-0">
              <img class="size-32 opacity-25 rounded-lg object-cover opacity-25" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo"/>
            </div>
          <img class="scale-y-125 size-32 rounded-lg shadow-lg" src="https://fastly.picsum.photos/id/866/600/600.jpg?hmac=dKQ662qAsYvwCo90SUl-0iG9K0Yp3ySEK0wJVIYh5uo" alt="mountains" />
          </div>
        </div>
      </div>
      `
        },
    ]
};

export default scale;
<div className="scale-75 -scale-75 scale-100 scale-125 scale-x-75 -scale-x-75 scale-x-100 scale-x-125 scale-y-75 -scale-y-75 scale-y-100 scale-y-125"></div>